// Sub-pages of the Bezug tab for the bean library (concept beans-and-shot-history §4 B, C, D): the open beans and the
// archive, one bean with its bags, recipe versions and earlier names, and the sheets to create a bean, edit its master
// data and add a bag. The cloud owns all of it; choosing a bean brings it onto the machine. Calm (19.10.2026): a button
// works while its request is out, success shows in the data itself, a failure in plain words next to it.
import { useEffect, useId, useState } from 'react';
import type { BeanItem } from '@brewcore/app-contract';
import {
  beanNameCheck,
  beanPage,
  dayFromIso,
  isoOfDay,
  libraryView,
  listErrorText,
  reasonText,
  type BeanFigures,
  type BrewPage,
  type LibraryRow,
  type LibraryTab,
} from '../../core/index.js';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  Chip,
  Collapse,
  DateField,
  Disclosure,
  Icon,
  IconButton,
  InfoTip,
  LinkSelect,
  Pending,
  Segmented,
  Sheet,
  Stepper,
  TextField,
} from '../components/index.js';
import { BeanInfo } from '../ai/BeanInfo.js';
import { useMachine, useStore } from '../machine-context.js';
import { AskSheet, Row, SubScreen } from './parts.js';

/** A request of these pages: the busy button and the failure in plain words. */
function useRequest() {
  const { ctx } = useMachine();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (
    key: string,
    r: () => Promise<{ ok: boolean; error?: { code: string } }>,
  ): Promise<boolean> => {
    setBusy(key);
    setError(null);
    const x = await r();
    setBusy(null);
    if (!x.ok) setError(reasonText(ctx.lang, x.error?.code ?? 'internal'));
    return x.ok;
  };
  return { busy, error, run, clear: () => setError(null) };
}

/**
 * The beans (board "Bohnen"): "Aktiv · Archiv" over the list, the beans in a card of their own, "+ Neue Bohne" under it
 * (it opens the page "Neue Bohne").
 */
export function BeansPage({ onPage, onBack }: { onPage: (p: BrewPage) => void; onBack: () => void }) {
  const { ctx, client, machineId } = useMachine();
  const store = client.beans(machineId);
  const bv = useStore(store);
  const sv = useStore(client.shots(machineId));
  const req = useRequest();
  const [tab, setTab] = useState<LibraryTab>('open');
  const v = libraryView(ctx, bv, tab, sv);
  return (
    <SubScreen tab="bezug" title={v.title} onBack={onBack} testId="beans-page">
      <Segmented label={v.title} options={v.tabs} onPick={setTab} testId="beans-tab" />
      <Card className="tab-card" data-testid="beans-library">
        {v.loading && <Pending label={ctx.tr('loading')} />}
        {v.empty && <p className="hint">{v.empty}</p>}
        {v.error && <Banner tone="danger">{listErrorText(ctx.lang, v.error)}</Banner>}
        {req.error && (
          <p className="field-error" role="alert">
            {req.error}
          </p>
        )}
        {v.items.length > 0 && (
          <ul className="list">
            {v.items.map((b) =>
              v.unarchive ? (
                // the archive (board "Bohnen"): the bean comes back with one tap, its page still opens
                <li key={b.id} className="list-row bean-row" data-testid={`library-${b.id}`}>
                  <button
                    type="button"
                    className="row-text row-link"
                    onClick={() => onPage({ page: 'bean', id: b.id })}
                  >
                    <BeanRowText b={b} />
                  </button>
                  <Button
                    variant="secondary"
                    size="sm"
                    busy={req.busy === b.id}
                    disabled={req.busy !== null}
                    onClick={() => void req.run(b.id, () => store.update(b.id, { archived: false }))}
                    data-testid={`library-unarchive-${b.id}`}
                  >
                    {v.unarchive}
                  </Button>
                </li>
              ) : (
                <li key={b.id} className="list-block">
                  <button
                    type="button"
                    className="list-head bean-row"
                    onClick={() => onPage({ page: 'bean', id: b.id })}
                    data-testid={`library-${b.id}`}
                  >
                    <span className="row-text">
                      <BeanRowText b={b} />
                    </span>
                    <Icon name="chevronRight" size="sm" className="list-chevron" />
                  </button>
                </li>
              ),
            )}
          </ul>
        )}
      </Card>
      {tab === 'open' && (
        <Button
          variant="secondary"
          block
          icon="plus"
          onClick={() => onPage({ page: 'newBean' })}
          data-testid="library-add"
        >
          {v.add}
        </Button>
      )}
    </SubScreen>
  );
}

/**
 * A bean in two lines that never wrap (Dominik 23.10.2026): the name with "In der Mühle" and the day of its last shot;
 * below the roaster, freshness and what is left. The roaster comes last in the markup and drops out of the line when
 * it has no room (row-reverse + wrap, one line high), so the figures stay and nothing is cut with "…".
 */
function BeanRowText({ b }: { b: LibraryRow }) {
  return (
    <>
      <span className="bean-row-top">
        <span className="row-label bean-row-name">{b.name}</span>
        {b.marker && (
          <Chip tone={b.marker.tone} className="bean-row-chip">
            {b.marker.text}
          </Chip>
        )}
        {b.last && <small className="hint bean-row-when">{b.last}</small>}
      </span>
      {(b.roaster || b.state) && (
        <span className="hint bean-row-sub">
          {b.state && <span className="num">{b.state}</span>}
          {b.roaster && <span>{b.state ? `${b.roaster}\u00a0·\u00a0` : b.roaster}</span>}
        </span>
      )}
    </>
  );
}

/**
 * "Neue Bohne" as a page (board "Neue Bohne"; concept §4 D): name, roast date and the bag's weight, more details behind
 * one row, where its recipe comes from as a row with its value; "Anlegen und wählen", "Anlegen", "Abbrechen". The bean
 * starts with its first bag (the roast date and the weight), so the beans page knows what is left. The one way to create
 * a bean (UX line 19.10.2026): from the bean list of the recipe and from the page Bohnen.
 */
export function NewBeanPage({
  onBack,
  onCreated,
}: {
  onBack: () => void;
  /** the new bean's page instead of this one (the history entry is replaced, back leads to the beans) */
  onCreated: (b: BeanItem) => void;
}) {
  const { ctx, client, machineId, stepper } = useMachine();
  const store = client.beans(machineId);
  const bv = useStore(store);
  const req = useRequest();
  const [name, setName] = useState('');
  const [roast, setRoast] = useState('');
  const [weight, setWeight] = useState(BAG_G);
  const [more, setMore] = useState(false);
  const [roaster, setRoaster] = useState('');
  const [origin, setOrigin] = useState('');
  const [process, setProcess] = useState('');
  const [from, setFrom] = useState('current');
  const ids = {
    name: useId(),
    roast: useId(),
    from: useId(),
    r: useId(),
    o: useId(),
    p: useId(),
  };
  const check = beanNameCheck(ctx, name, bv);
  const sources = bv.items.filter((b) => b.recipe);
  const fromText =
    from === 'current'
      ? ctx.tr('recipeFromCurrent')
      : ctx.tr('recipeFromBean', { n: sources.find((b) => b.id === from)?.name ?? '' });
  const create = async (use: boolean) => {
    // values still collected by −/+ go out first: "wie jetzt eingestellt" are the ones on screen
    await stepper.flush();
    const day = dayFromIso(roast);
    let created: BeanItem | null = null;
    const ok = await req.run(use ? 'use' : 'create', async () => {
      const r = await store.create({
        name: name.trim(),
        ...(roaster.trim() ? { roaster: roaster.trim() } : {}),
        ...(origin.trim() ? { origin: origin.trim() } : {}),
        ...(process.trim() ? { process: process.trim() } : {}),
        recipeFrom: from,
      });
      if (r.ok) created = r.value;
      return r;
    });
    if (!ok || !created) return;
    const bean: BeanItem = created;
    // its first bag with the roast date and the weight (board: "Packung 250 g"); a roast date alone in the create
    // request would start a bag without its weight
    await req.run('bag', () =>
      store.addBag(bean.id, { ...(day !== null ? { roastDay: day } : {}), weightG: weight }),
    );
    if (use) await req.run('use', () => store.select(bean.id));
    onCreated(bean);
  };
  return (
    <SubScreen tab="bezug" title={ctx.tr('beanAdd')} onBack={onBack} testId="new-bean-page">
      <Card className="tab-card" data-testid="new-bean-card">
        <TextField
          id={ids.name}
          label={ctx.tr('beanName')}
          value={name}
          autoFocus
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          error={check.error ?? undefined}
          data-testid="new-bean-name"
        />
        <DateField
          id={ids.roast}
          label={ctx.tr('roast')}
          value={roast}
          max={isoOfDay(ctx.today)}
          placeholder={ctx.tr('dateChoose')}
          onChange={(e) => setRoast(e.target.value)}
          data-testid="new-bean-roast"
        />
        <BagWeightRow weight={weight} onWeight={setWeight} label={ctx.tr('bagAdd')} />
      </Card>
      <Card className="tab-card more-card" data-testid="new-bean-more-card">
        <Disclosure open={more} onClick={() => setMore(!more)} data-testid="new-bean-more">
          {ctx.tr('moreDetails')}
        </Disclosure>
        <Collapse open={more}>
          <div className="stack new-bean-more">
            <TextField
              id={ids.r}
              label={ctx.tr('roaster')}
              value={roaster}
              maxLength={60}
              onChange={(e) => setRoaster(e.target.value)}
            />
            <TextField
              id={ids.o}
              label={ctx.tr('origin')}
              value={origin}
              maxLength={60}
              onChange={(e) => setOrigin(e.target.value)}
            />
            <TextField
              id={ids.p}
              label={ctx.tr('process')}
              value={process}
              maxLength={60}
              onChange={(e) => setProcess(e.target.value)}
            />
          </div>
        </Collapse>
      </Card>
      <Card className="tab-card rows-card" data-testid="new-bean-recipe">
        <Row label={ctx.tr('recipeFrom')} info={ctx.tr('recipeFromInfo')} testId="new-bean-from-row">
          <LinkSelect
            id={ids.from}
            label={ctx.tr('recipeFrom')}
            text={fromText}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            data-testid="new-bean-from"
          >
            <option value="current">{ctx.tr('recipeFromCurrent')}</option>
            {sources.map((b) => (
              <option key={b.id} value={b.id}>
                {ctx.tr('recipeFromBean', { n: b.name })}
              </option>
            ))}
          </LinkSelect>
        </Row>
      </Card>
      {req.error && (
        <p className="field-error" role="alert">
          {req.error}
        </p>
      )}
      <div className="page-actions">
        <Button
          block
          disabled={!check.ok || req.busy !== null}
          busy={req.busy === 'use'}
          onClick={() => void create(true)}
          data-testid="new-bean-use"
        >
          {ctx.tr('beanCreateUse')}
        </Button>
        <Button
          variant="secondary"
          block
          disabled={!check.ok || req.busy !== null}
          busy={req.busy === 'create'}
          onClick={() => void create(false)}
          data-testid="new-bean-create"
        >
          {ctx.tr('beanCreate')}
        </Button>
        <button type="button" className="link-btn page-cancel" onClick={onBack} data-testid="new-bean-cancel">
          {ctx.tr('cancel')}
        </button>
      </div>
    </SubScreen>
  );
}

/** The bag's weight as a row with −/+ (board "Neue Bohne": "Packung − 250 g +"), in steps of 50 g. */
function BagWeightRow({
  weight,
  onWeight,
  label,
}: {
  weight: number;
  onWeight: (w: number) => void;
  label: string;
}) {
  const { ctx } = useMachine();
  return (
    <div className="row" data-testid="bag-weight-row">
      <span className="row-label">{label}</span>
      <Stepper
        value={`${weight}\u00a0g`}
        onMinus={() => onWeight(Math.max(BAG_STEP_G, weight - BAG_STEP_G))}
        onPlus={() => onWeight(Math.min(BAG_MAX_G, weight + BAG_STEP_G))}
        minusLabel={`${label} ${ctx.tr('less')}`}
        plusLabel={`${label} ${ctx.tr('more')}`}
        valueTestId="bag-weight"
      />
    </div>
  );
}

export function BeanPage({
  id,
  onBack,
  onOpenShot,
}: {
  id: string;
  onBack: () => void;
  onOpenShot: (shotId: string) => void;
}) {
  const { ctx, client, machineId } = useMachine();
  const store = client.beans(machineId);
  const bv = useStore(store);
  const req = useRequest();
  const [editing, setEditing] = useState(false);
  const [addingBag, setAddingBag] = useState(false);
  const [asking, setAsking] = useState<'delete' | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  // the bag just emptied: "Packung ist leer." with "Rückgängig" over the tab bar for a few seconds
  const [undoBag, setUndoBag] = useState<string | null>(null);
  const askId = useId();
  useEffect(() => {
    void store.detail(id);
  }, [store, id]);
  const bean = store.find(id);
  const shots = client.shots(machineId);
  const sv = useStore(shots);
  const refId = bean?.referenceShotId ?? null;
  const refShot = refId ? (sv.curves[refId] ?? sv.items.find((s) => s.id === refId) ?? null) : null;
  useEffect(() => {
    if (refId && !refShot) void shots.curve(refId);
  }, [refId, refShot, shots]);
  // the figures of the bean (all its shots, how many balanced, the mean time); again after a new shot of it
  const [figures, setFigures] = useState<BeanFigures | null>(null);
  const shotCount = bean?.shotCount ?? 0;
  useEffect(() => {
    let live = true;
    if (shotCount > 0) void shots.beanFigures(id).then((f) => live && setFigures(f));
    return () => {
      live = false;
    };
  }, [shots, id, shotCount]);
  useEffect(() => {
    if (!undoBag) return;
    const t = setTimeout(() => setUndoBag(null), UNDO_MS);
    return () => clearTimeout(t);
  }, [undoBag]);
  if (!bean)
    return (
      <SubScreen tab="bezug" title={ctx.tr('beansTitle')} onBack={onBack} testId="bean-page">
        <Card className="tab-card">
          {bv.error ? (
            <Banner tone="danger">{listErrorText(ctx.lang, bv.error)}</Banner>
          ) : (
            <Pending label={ctx.tr('loading')} />
          )}
        </Card>
      </SubScreen>
    );
  const v = beanPage(ctx, bean, refShot, figures, sv);
  const emptyBag = async (bagId: string) => {
    if (await req.run(`bag-${bagId}`, () => store.updateBag(id, bagId, { emptied: true }))) setUndoBag(bagId);
  };
  const undo = (bagId: string) => {
    setUndoBag(null);
    void req.run(`bag-${bagId}`, () => store.updateBag(id, bagId, { emptied: false }));
  };
  return (
    <SubScreen
      tab="bezug"
      title={v.title}
      sub={
        (v.sub || v.marker) && (
          <span className="bean-sub">
            {v.sub && <span>{v.sub}</span>}
            {v.marker && (
              <Chip tone={v.marker.tone} className="bean-row-chip" data-testid="bean-marker">
                {v.marker.text}
              </Chip>
            )}
          </span>
        )
      }
      onBack={onBack}
      testId="bean-page"
      right={
        <button type="button" className="link-btn" onClick={() => setEditing(true)} data-testid="bean-edit">
          {v.actions.edit}
        </button>
      }
    >
      {(v.figures.length > 0 || v.origin || v.reference || v.notes) && (
        <Card className="tab-card" data-testid="bean-head">
          {v.figures.length > 0 && (
            <dl className="fact-row is-rows" data-testid="bean-figures">
              {v.figures.map((f) => (
                <div key={f.key} className="fact">
                  <dt>{f.label}</dt>
                  <dd className="num" data-testid={`bean-${f.key}`}>
                    {f.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {(v.origin || v.reference) && (
            <ul className="list bean-head-rows">
              {v.origin && (
                <li className="list-row" data-testid="bean-origin">
                  <span className="row-text">
                    <span className="row-label">{v.origin.label}</span>
                    <span className="hint">{v.origin.value}</span>
                  </span>
                </li>
              )}
              {v.reference && (
                <li className="list-row" data-testid="bean-reference">
                  <span className="row-text">
                    <span className="title-info">
                      <span className="row-label">{v.reference.label}</span>
                      <InfoTip
                        text={v.reference.info}
                        label={ctx.tr('infoShow')}
                        testId="bean-reference-info"
                      />
                    </span>
                    <span className="hint num">{v.reference.text}</span>
                  </span>
                  <IconButton
                    icon="chevronRight"
                    label={ctx.tr('beanReferenceOpen')}
                    className="list-chevron-btn"
                    onClick={() => v.reference && onOpenShot(v.reference.shotId)}
                    data-testid="bean-reference-open"
                  />
                </li>
              )}
            </ul>
          )}
          {v.notes && <p className="text-dim">{v.notes}</p>}
        </Card>
      )}
      <BeanInfo bean={bean} />
      <Card className="tab-card" data-testid="bean-bags">
        <CardHeader
          title={v.bags.title}
          action={
            <button
              type="button"
              className="link-btn"
              onClick={() => setAddingBag(true)}
              data-testid="bag-add"
            >
              <Icon name="plus" size="sm" />
              {v.actions.addBag}
            </button>
          }
        />
        {v.bags.items.length === 0 && <p className="hint">{v.bags.empty}</p>}
        <ul className="list">
          {v.bags.items.map((g) => (
            <li key={g.id} className="list-row" data-testid={`bag-${g.id}`}>
              <span className="row-text">
                <span className={g.empty ? 'row-label' : 'row-label text-dim'}>{g.label}</span>
                {g.text && <span className="hint num">{g.text}</span>}
              </span>
              {g.empty && (
                <Button
                  variant="secondary"
                  size="sm"
                  busy={req.busy === `bag-${g.id}`}
                  disabled={req.busy !== null}
                  onClick={() => void emptyBag(g.id)}
                  data-testid={`bag-empty-${g.id}`}
                >
                  {g.empty}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </Card>
      {v.recipe && (
        <Card className="tab-card" data-testid="bean-recipe">
          <CardHeader title={v.recipe.title} action={<span className="hint">{v.recipe.version}</span>} />
          <dl className="fact-row is-rows">
            {v.recipe.facts.map((f) => (
              <div key={f.key} className="fact">
                <dt>{f.label}</dt>
                <dd className="num">{f.value}</dd>
              </div>
            ))}
          </dl>
          {v.versions && (
            <>
              {/* the earlier versions behind one row (Dominik 21.10.2026: side by side they got confusing) */}
              <Disclosure
                open={versionsOpen}
                className="bean-versions-toggle"
                onClick={() => setVersionsOpen((o) => !o)}
                data-testid="bean-versions"
              >
                <span className="row-label">{v.versions.title}</span>
                <span className="hint num">{v.versions.count}</span>
              </Disclosure>
              <Collapse open={versionsOpen}>
                <ul className="list bean-versions">
                  {v.versions.items.map((r) => (
                    <li key={r.id} className="list-row" data-testid={`version-${r.n}`}>
                      <span className="row-text">
                        <span className="row-label num">{r.change || r.label}</span>
                        {r.change && <span className="hint">{r.label}</span>}
                      </span>
                      <small className="hint">{r.when}</small>
                    </li>
                  ))}
                </ul>
              </Collapse>
            </>
          )}
          {v.loading && <Pending label={ctx.tr('loading')} />}
        </Card>
      )}
      <div className="page-actions">
        {req.error && (
          <p className="field-error" role="alert" data-testid="bean-error">
            {req.error}
          </p>
        )}
        {v.actions.select && (
          <Button
            block
            busy={req.busy === 'select'}
            disabled={req.busy !== null || !ctx.controllable}
            onClick={() => void req.run('select', () => store.select(id))}
            data-testid="bean-select"
          >
            {v.actions.select}
          </Button>
        )}
        {v.actions.archive && (
          <Button
            variant="secondary"
            block
            busy={req.busy === 'archive'}
            disabled={req.busy !== null}
            onClick={() =>
              void req.run('archive', () => store.update(id, { archived: !!v.actions.archive?.archived }))
            }
            data-testid="bean-archive"
          >
            {v.actions.archive.label}
          </Button>
        )}
        {/* destructive, apart at the end of the page (UX line) */}
        {v.actions.remove && (
          <button
            type="button"
            className="link-btn danger-link"
            disabled={req.busy !== null}
            onClick={() => setAsking('delete')}
            data-testid="bean-delete"
          >
            {v.actions.remove.label}
          </button>
        )}
      </div>
      {undoBag && (
        <div className="toast toast-info toast-undo" role="status" aria-live="polite" data-testid="bag-undo">
          <span>{ctx.tr('bagEmptied')}</span>
          <button
            type="button"
            className="toast-action"
            onClick={() => undo(undoBag)}
            data-testid="bag-undo-go"
          >
            {ctx.tr('undo')}
          </button>
        </div>
      )}
      {editing && <EditBeanSheet bean={bean} onClose={() => setEditing(false)} />}
      {addingBag && <BagSheet beanId={id} onClose={() => setAddingBag(false)} />}
      {asking === 'delete' && v.actions.remove && (
        <AskSheet
          ask={{
            title: v.actions.remove.title,
            text: v.actions.remove.ask,
            ok: v.actions.remove.ok,
            danger: true,
          }}
          titleId={askId}
          onNo={() => setAsking(null)}
          onYes={() => {
            setAsking(null);
            void req.run('delete', () => store.remove(id)).then((ok) => ok && onBack());
          }}
          testId="bean-delete"
        />
      )}
    </SubScreen>
  );
}

/** How long "Packung ist leer." with "Rückgängig" stays over the tab bar. */
const UNDO_MS = 6000;

/** Name and master data; the name of a bean on the machine changes there too (the API sends bean.rename). */
function EditBeanSheet({ bean, onClose }: { bean: BeanItem; onClose: () => void }) {
  const { ctx, client, machineId } = useMachine();
  const store = client.beans(machineId);
  const bv = useStore(store);
  const req = useRequest();
  const [name, setName] = useState(bean.name);
  const [roaster, setRoaster] = useState(bean.roaster ?? '');
  const [origin, setOrigin] = useState(bean.origin ?? '');
  const [process, setProcess] = useState(bean.process ?? '');
  const [notes, setNotes] = useState(bean.notes ?? '');
  const ids = { title: useId(), name: useId(), r: useId(), o: useId(), p: useId(), n: useId() };
  const check = beanNameCheck(ctx, name, bv, bean.id);
  const save = async () => {
    const text = (s: string) => (s.trim() ? s.trim() : null);
    const ok = await req.run('save', () =>
      store.update(bean.id, {
        ...(name.trim() !== bean.name ? { name: name.trim() } : {}),
        roaster: text(roaster),
        origin: text(origin),
        process: text(process),
        notes: text(notes),
        ifRev: bean.cloudRev,
      }),
    );
    if (ok) onClose();
  };
  return (
    <Sheet title={ctx.tr('beanEditTitle')} titleId={ids.title} onClose={onClose} testId="edit-bean-sheet">
      <TextField
        id={ids.name}
        label={ctx.tr('beanName')}
        value={name}
        maxLength={60}
        onChange={(e) => setName(e.target.value)}
        error={check.error ?? undefined}
        data-testid="edit-bean-name"
      />
      <TextField
        id={ids.r}
        label={ctx.tr('roaster')}
        value={roaster}
        maxLength={60}
        onChange={(e) => setRoaster(e.target.value)}
        data-testid="edit-bean-roaster"
      />
      <TextField
        id={ids.o}
        label={ctx.tr('origin')}
        value={origin}
        maxLength={60}
        onChange={(e) => setOrigin(e.target.value)}
      />
      <TextField
        id={ids.p}
        label={ctx.tr('process')}
        value={process}
        maxLength={60}
        onChange={(e) => setProcess(e.target.value)}
      />
      <div className="field">
        <label className="field-label" htmlFor={ids.n}>
          {ctx.tr('note')}
        </label>
        <textarea
          id={ids.n}
          className="field-input field-area"
          rows={2}
          maxLength={500}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
      {req.error && (
        <p className="field-error" role="alert">
          {req.error}
        </p>
      )}
      <div className="sheet-actions">
        <Button
          block
          disabled={!check.ok || req.busy !== null}
          busy={req.busy === 'save'}
          onClick={() => void save()}
          data-testid="edit-bean-save"
        >
          {ctx.tr('save')}
        </Button>
        <Button variant="secondary" block onClick={onClose}>
          {ctx.tr('cancel')}
        </Button>
      </div>
    </Sheet>
  );
}

/**
 * A new bag (board "Eine Bohne", Dominik 23.10.2026: "nachkaufen wuerde nur sinn machen wenn man dann auch ein neues
 * roestdatum eingeben kann"): the roast date and the weight; it becomes the active one, an archived bean comes back.
 */
function BagSheet({ beanId, onClose }: { beanId: string; onClose: () => void }) {
  const { ctx, client, machineId } = useMachine();
  const store = client.beans(machineId);
  const req = useRequest();
  const [roast, setRoast] = useState('');
  const [weight, setWeight] = useState(BAG_G);
  const ids = { title: useId(), roast: useId() };
  const save = async () => {
    const r = dayFromIso(roast);
    const ok = await req.run('save', () =>
      store.addBag(beanId, { ...(r !== null ? { roastDay: r } : {}), weightG: weight }),
    );
    if (ok) onClose();
  };
  return (
    <Sheet
      title={ctx.tr('bagNew')}
      titleId={ids.title}
      onClose={onClose}
      closeLabel={ctx.tr('cancel')}
      closeTestId="bag-cancel"
      testId="bag-sheet"
    >
      <div className="stack">
        <DateField
          id={ids.roast}
          label={ctx.tr('roast')}
          value={roast}
          max={isoOfDay(ctx.today)}
          placeholder={ctx.tr('dateChoose')}
          onChange={(e) => setRoast(e.target.value)}
          data-testid="bag-roast"
        />
        <BagWeightRow weight={weight} onWeight={setWeight} label={ctx.tr('bagWeight')} />
        {req.error && (
          <p className="field-error" role="alert">
            {req.error}
          </p>
        )}
        <Button
          block
          disabled={req.busy !== null}
          busy={req.busy === 'save'}
          onClick={() => void save()}
          data-testid="bag-save"
        >
          {ctx.tr('bagAddGo')}
        </Button>
      </div>
    </Sheet>
  );
}

/** a bag as it is usually sold, in steps of 50 g up to 2 kg */
const BAG_G = 250;
const BAG_STEP_G = 50;
const BAG_MAX_G = 2000;
